import { Component, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { NotificationService } from "src/app/notifications/notification.service";
import { PermissionService } from "src/app/permission.service";
import { SessionService } from "src/app/session.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { FilterTableDialogComponent } from "src/app/shared/dialogs/filter-table-dialog/filter-table-dialog.component";
import { UiService } from "src/app/ui/ui.service";
import { UsersService } from "src/app/user/users.service";
import { UtilService } from "src/app/util/util.service";
import { AccountDTO } from "../Account";
import { AccountService } from "../account.service";

@Component({
  selector: "app-accounts-list",
  templateUrl: "./accounts-list.component.html",
  styleUrls: ["./accounts-list.component.scss"],
})
export class AccountsListComponent implements OnInit {
  searchValue: string = "";
  accounts!: AccountDTO[];
  accountDataSource!: MatTableDataSource<any>;
  columns = [
    { code: "id", name: "ID", filter: true, selected: true },
    { code: "name", name: "Account Name", filter: true, selected: true },
    {
      code: "created_by_name",
      name: "Created By",
      filter: true,
      selected: true,
    },
    { code: "created_at", name: "Creation Date", filter: true, selected: true },
    { code: "options" },
  ];
  displayedColumns = this.columns.map((column) => column.code);

  @ViewChild("accountsTable") accountsTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  showEdit: boolean = false;
  showNewAccount: boolean = false;
  showDelete: boolean = false;
  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private AccountService: AccountService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private PermissionService: PermissionService,
    private uiService: UiService,
    private UserService: UsersService,
    private utilSvc: UtilService,
  ) {}

  ngOnInit(): void {
    this.accounts = this.activatedRoute.snapshot.data.accounts.records;
    this.accountDataSource = new MatTableDataSource(this.accounts);

    this.accountDataSource.filterPredicate = (data: any, filter: string) => {
      let found = false;
      const selectedFilterColumns = this.columns
        .filter((column) => column.selected)
        .map((column) => column.code);

      for (let column of selectedFilterColumns) {
        let rowValue = data[column];
        if (typeof rowValue === "number") {
          rowValue = rowValue.toString();
        }
        if (rowValue) {
          if (rowValue?.trim().toLowerCase().indexOf(filter) !== -1) {
            found = true;
          }
        }
      }

      return found;
    };

    Promise.all([
      this.PermissionService.categories.accounts.hasWrite(),
      this.PermissionService.categories.accounts.hasDetail(),
    ]).then(([hasWrite, hasDetail]) => {
      this.showEdit = hasWrite && hasDetail;
      this.showNewAccount = hasWrite;
      this.showDelete = hasWrite;
    });
  }

  ngAfterViewInit() {
    this.accountsTable.renderRows();
    this.accountDataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue;
  }

  get currentUser() {
    return this.SessionService.currentUserValue;
  }

  formatDate = (date: Date) => {
    return this.utilSvc.formatDate(date);
  }

  applyFilter() {
    this.accountDataSource.filter = this.searchValue.trim().toLowerCase();
  }

  createNewAccount() {
    this.router.navigate(["create"], {
      relativeTo: this.activatedRoute.parent,
    });
  }

  isCurrentAccount(accountDTO: AccountDTO) {
    return this.currentAccount!.accountID === accountDTO.id;
  }

  toggleFilterDialog() {
    this.dialog
      .open(FilterTableDialogComponent, {
        data: {
          columns: this.columns,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.columns = result;
          this.applyFilter();
        }
      });
  }

  edit(account: AccountDTO) {
    this.router.navigate([account.id], {
      relativeTo: this.activatedRoute.parent,
    });
  }

  delete(account: AccountDTO) {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this account ( ${account.name} )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.AccountService.deleteAccount(
                this.currentAccount!.accountID,
                account.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.NotificationService.openSuccessSnackBar("Account deleted");
                this.uiService
                  .waitForEvent(
                    this.AccountService.getAccounts(
                      this.currentAccount!.accountID,
                      {
                        ignoreAccountUsers: true,
                      },
                    ),
                  )
                  .pipe(takeUntil(this.onComponentDestroy))
                  .subscribe((accounts) => {
                    this.accounts = (accounts as any).records as AccountDTO[];
                    this.accountDataSource = new MatTableDataSource(
                      this.accounts,
                    );
                    this.accountsTable.renderRows();
                    this.accountDataSource.sort = this.sort;
                  });

                this.UserService.getUser(
                  this.currentAccount!.accountID,
                  this.currentUser!.id,
                )
                  .pipe(takeUntil(this.onComponentDestroy))
                  .subscribe((user: any) => {
                    this.SessionService.updateAccounts(user);
                  });
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error deleting account",
                );
              },
            );
        }
      });
  }
}
